import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useNavigate } from 'react-router-dom';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  BarChart, Bar, Legend, Cell 
} from 'recharts';

const AdminPage = () => {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [stats, setStats] = useState({ users: 0, applications: 0, dogs: 0, clicks: 0 });
  const [chartData, setChartData] = useState([]);
  const [eventData, setEventData] = useState([]);
  const [visitStats, setVisitStats] = useState({ todayCount: 0, data: [], referrers: [], keywords: [] });
  const [selectedDate, setSelectedDate] = useState(null);
  const [visitPeriod, setVisitPeriod] = useState('daily');
  const [applications, setApplications] = useState([]);
  const [users, setUsers] = useState([]);
  const [dogs, setDogs] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [reports, setReports] = useState([]);
  const [adRequests, setAdRequests] = useState([]);
  const [payments, setPayments] = useState([]);
  const [banners, setBanners] = useState({});
  const [bannerUploadForm, setBannerUploadForm] = useState({ slot_key: '', image_url: '', link_url: '' });
  const [bannerUploadFile, setBannerUploadFile] = useState(null);
  const [isBannerUploading, setIsBannerUploading] = useState(false);
  const navigate = useNavigate();

  // 쿠폰 생성용 상태
  const [newCoupon, setNewCoupon] = useState({ 
    code: '', 
    name: '', 
    amount: 0, 
    benefit_type: 'ad_exemption', 
    auto_issue_type: 'none',
    valid_until: '',
    ad_type: 'all'
  });
  const [issueData, setIssueData] = useState({ userId: '', couponId: '' });
  const [issueTargetType, setIssueTargetType] = useState('individual'); // 'individual', 'all_sellers'
  const [userSearch, setUserSearch] = useState('');
  const [isIssuing, setIsIssuing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  async function checkAdmin() {
    const { data: sessionData, error: sessionErr } = await api.auth.getSession();
    if (sessionErr || !sessionData?.session) return navigate('/login');

    const { data: profile } = await api.auth.getUser();

    if (profile?.role !== 'admin') {
      alert('관리자만 접근 가능합니다.');
      return navigate('/');
    }

    setIsAdmin(true);
    // 진입 시 이용권 만료 처리
    await api.admin.expireAds();
    fetchAdminData();
  }

  async function fetchVisitData(period, exactLabel = null) {
    const { data } = await api.admin.getVisits(period, exactLabel);
    if (data && data.success) {
      setVisitStats(prev => ({ 
        todayCount: exactLabel ? prev.todayCount : data.todayCount, 
        data: exactLabel ? prev.data : data.data,
        referrers: data.referrers || [],
        keywords: data.keywords || []
      }));
      if (!exactLabel) setVisitPeriod(period);
      setSelectedDate(exactLabel);
    }
  }

  async function fetchAdminData() {
    setLoading(true);

    try {
      fetchVisitData('daily');
      // 1. 요약 통계 및 차트
      const { data: statsData } = await api.admin.getStats();
      if (statsData) {
        setStats({ 
          users: statsData.userCount || 0, 
          applications: statsData.pendingApplications || 0, 
          dogs: statsData.dogCount || 0, 
          clicks: statsData.clickCount || 0 
        });
        setChartData(statsData.chartData || []);
        
        const totalViews = statsData.chartData?.reduce((acc, c) => acc + c.views, 0) || 0;
        const totalClicks = statsData.chartData?.reduce((acc, c) => acc + c.clicks, 0) || 0;
        setEventData([
          { name: '페이지 뷰', value: totalViews },
          { name: '클릭 액션', value: totalClicks },
        ]);
      }

      // 2. 신청 리스트
      const { data: apps } = await api.admin.getApplications();
      setApplications(apps || []);

      // 3. 전체 회원
      const { data: userList } = await api.admin.getUsers();
      setUsers(userList || []);

      // 4. 전체 게시물
      const { data: dogList } = await api.admin.getDogs();
      setDogs(dogList || []);

      // 5. 쿠폰 목록
      const { data: couponList } = await api.admin.getCoupons();
      setCoupons(couponList || []);

      // 6. 신고 내역 목록
      const { data: reportList } = await api.admin.getReports();
      setReports(reportList || []);

      // 7. 서비스 신청 내역
      const { data: adReqList } = await api.admin.getAdRequests();
      setAdRequests(adReqList || []);

      // 8. 시스템 배너 목록
      const { data: bannerData } = await api.banners.getList();
      if (bannerData) setBanners(bannerData);

      // 9. 전체 결제 목록
      const { data: payList } = await api.admin.getPayments();
      setPayments(payList || []);

    } catch (error) {
      console.error('Data fetch error:', error);
    }

    setLoading(false);
  }

  useEffect(() => {
    setTimeout(() => checkAdmin(), 0);
  }, []);

  const handleApprove = async (app) => {
    if (!window.confirm(`${app.business_name} 승인하시겠습니까?`)) return;
    
    const { error } = await api.admin.approveApplication(app.id, app.user_id);
    if (error) {
      alert('승인 처리 중 오류가 발생했습니다: ' + error);
    } else {
      alert('승인되었습니다. (자동 쿠폰 지급 완료)');
      fetchAdminData();
    }
  };

  const handleReject = async (app) => {
    const reason = window.prompt(`${app.business_name} 신청을 반려하시겠습니까?\n반려 사유를 입력해 주세요:`);
    if (reason === null) return; // 사용자가 취소한 경우

    const { error } = await api.admin.rejectApplication(app.id, reason || '관리자 반려');
    if (error) {
      alert('반려 처리 중 오류가 발생했습니다: ' + error);
    } else {
      alert('반려 처리되었습니다.');
      fetchAdminData();
    }
  };

  const handleUpdateGrade = async (userId, newGrade) => {
    const { error } = await api.admin.updateUserGrade(userId, newGrade);
    if (error) {
      alert('등급 변경 실패: ' + error);
    } else {
      alert('등급이 변경되었습니다.');
      fetchAdminData();
    }
  };

  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    if (isCreating) return;
    setIsCreating(true);

    const { error } = await api.admin.createCoupon({
      code: newCoupon.code,
      name: newCoupon.name,
      discount_rate: parseInt(newCoupon.amount),
      auto_issue_type: newCoupon.auto_issue_type,
      valid_until: newCoupon.valid_until || null,
      ad_type: newCoupon.ad_type
    });

    setIsCreating(false);
    if (error) {
      alert('생성 실패: ' + error);
    } else {
      alert('서비스 이용권이 생성되었습니다.');
      setNewCoupon({ code: '', name: '', amount: 0, benefit_type: 'ad_exemption', auto_issue_type: 'none', valid_until: '', ad_type: 'all' });
      fetchAdminData();
    }
  };

  const handleIssueCoupon = async (e) => {
    e.preventDefault();
    if (isIssuing) return;
    if (!issueData.couponId) return alert('발급할 쿠폰을 선택해 주세요.');

    setIsIssuing(true);

    if (issueTargetType === 'all_sellers') {
      if (!window.confirm('모든 사업자 회원에게 쿠폰을 일괄 발급하시겠습니까?')) {
        setIsIssuing(false);
        return;
      }
      
      const { error } = await api.admin.issueCouponToAll(parseInt(issueData.couponId));
      setIsIssuing(false);
      if (error) {
        return alert('일괄 발급 실패: ' + error);
      }
      alert('모든 사업자에게 쿠폰이 발급되었습니다.');
      setIssueData({ userId: '', couponId: '' });
      fetchAdminData();
    } else {
      // 개별 유저 발급
      if (!issueData.userId) {
        setIsIssuing(false);
        return alert('대상 유저를 선택해 주세요.');
      }

      const { error } = await api.admin.issueCouponToUser(parseInt(issueData.couponId), issueData.userId);
      setIsIssuing(false);
      if (error) {
        alert('발급 실패: ' + error);
      } else {
        alert('개별 쿠폰이 성공적으로 발급되었습니다.');
        setIssueData({ userId: '', couponId: '' });
        fetchAdminData();
      }
    }
  };

  const handleSendGlobalMessage = async () => {
    const message = window.prompt('모든 회원에게 발송할 공지 메시지를 입력하세요:');
    if (!message) return;

    if (!window.confirm('정말 모든 회원(일반, 사업자 포함)에게 알림을 발송하시겠습니까?')) return;

    const { error } = await api.admin.sendGlobalNotice(message);
    if (error) {
      alert('발송 실패: ' + error);
    } else {
      alert('성공적으로 모든 회원에게 공지 알림이 발송되었습니다!');
    }
  };

  const handleDeleteReportedDog = async (report) => {
    if (!window.confirm(`정말 해당 게시물(ID: ${report.dog_id})을 강제 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.`)) return;
    
    const { error } = await api.admin.deleteDog(report.dog_id);
    if (error) {
      alert('게시물 삭제 중 오류: ' + error);
    } else {
      alert('게시물이 삭제되었고, 판매자에게 알림이 전송되었습니다.');
      fetchAdminData();
    }
  };

  const handleApproveCancelPayment = async (payment) => {
    if (payment.status !== 'cancel_requested') return;
    
    let refundInfo = {};
    if (payment.pay_method === 'vbank') {
      const isConfirmed = window.confirm('가상계좌 결제건입니다. 환불받을 계좌 정보를 입력하시겠습니까?\n(미입력 시 기본 취소 요청만 진행됩니다.)');
      if (isConfirmed) {
        const bank = window.prompt('환불받을 은행명을 입력하세요 (예: 국민은행):');
        const account = window.prompt('환불받을 계좌번호를 입력하세요:');
        const holder = window.prompt('예금주명을 입력하세요:');
        if (bank && account && holder) {
          refundInfo = { refund_bank: bank, refund_account: account, refund_holder: holder };
        } else {
          alert('계좌 정보가 불완전하여 취소 승인이 중단되었습니다.');
          return;
        }
      }
    } else {
      if (!window.confirm('해당 결제 건의 취소를 승인하시겠습니까? 포트원을 통해 실결제 취소가 진행됩니다.')) return;
    }

    const { error } = await api.admin.approveCancelPayment(payment.id, refundInfo);
    if (error) {
      alert('결제 취소 처리 중 오류: ' + error);
    } else {
      alert('결제 취소가 승인되었습니다.');
      fetchAdminData();
    }
  };

  const handleDeletePost = async (dogId) => {
    const { error } = await api.admin.deleteDog(dogId);
    if (error) {
      alert('게시물 삭제 중 오류: ' + error);
    } else {
      alert('게시물이 강제 삭제되었고, 판매자에게 시스템 알림이 전송되었습니다.');
      fetchAdminData();
    }
  };

  const handleResolveReport = async (reportId) => {
    if (!window.confirm('게시물 삭제 없이 이 신고를 반려(패스)하시겠습니까?')) return;
    const { error } = await api.admin.resolveReport(reportId);
    if (error) {
      alert('처리 실패: ' + error);
    } else {
      alert('반려 처리되었습니다.');
      fetchAdminData();
    }
  };

  const handleUpdateAdRequestStatus = async (id, status) => {
    let confirmMsg = '상태를 변경하시겠습니까?';
    if (status === 'active') confirmMsg = '승인 처리하시겠습니까?\n세금계산서가 자동 발행되며 서비스 이용권 쿠폰이 발급됩니다.';
    if (status === 'rejected') confirmMsg = '정말 취소(반려) 처리하시겠습니까?';

    if (!window.confirm(confirmMsg)) return;

    const { data, error } = await api.admin.updateAdRequestStatus(id, status);
    if (error) {
      alert('상태 업데이트 중 오류가 발생했습니다: ' + error);
    } else {
      let msg = data?.message || '처리되었습니다.';
      if (data?.taxInvoiceIssued) msg += '\n🧾 세금계산서 발행 완료';
      alert(msg);
      fetchAdminData();
    }
  };

  const handleBannerSubmit = async (e) => {
    e.preventDefault();
    if (!bannerUploadForm.slot_key) {
      return alert('슬롯을 선택해 주세요.');
    }
    if (!bannerUploadFile && !bannerUploadForm.image_url) {
      return alert('배너 이미지를 첨부하거나 이미지 URL을 입력해 주세요.');
    }

    setIsBannerUploading(true);
    let finalImageUrl = bannerUploadForm.image_url;

    try {
      if (bannerUploadFile) {
        const { data: uploadData, error: uploadError } = await api.uploadFile(bannerUploadFile);
        if (uploadError) throw new Error(uploadError);
        finalImageUrl = uploadData.url;
      }

      const { error } = await api.banners.adminCreate({ ...bannerUploadForm, image_url: finalImageUrl });
      if (error) throw new Error(error);

      alert('배너가 정상적으로 등록되었습니다.');
      setBannerUploadForm({ slot_key: '', image_url: '', link_url: '' });
      setBannerUploadFile(null);
      // 파일 input 초기화
      const fileInput = document.getElementById('banner-file-input');
      if (fileInput) fileInput.value = '';
      
      fetchAdminData();
    } catch (err) {
      alert('배너 등록 실패: ' + err.message);
    } finally {
      setIsBannerUploading(false);
    }
  };

  const handleBannerDelete = async (id) => {
    if (!window.confirm('정말 이 배너를 삭제하시겠습니까?')) return;
    const { error } = await api.banners.adminDelete(id);
    if (error) {
      alert('배너 삭제 실패: ' + error);
    } else {
      alert('배너가 삭제되었습니다.');
      fetchAdminData();
    }
  };

  if (!isAdmin || loading) return <div style={fullCenterStyle}>데이터를 동기화 중입니다...</div>;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', backgroundColor: '#f4f7f6' }}>
      <nav style={sidebarStyle}>
        <div style={{ padding: '30px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <h2 style={{ color: 'white', fontSize: '1.2rem', fontWeight: '800' }}>팔도댕댕 어드민 v1.0</h2>
        </div>
        <div style={{ marginTop: '20px' }}>
          {[
            { id: 'summary', icon: '📊', label: '활동 통계' },
            { id: 'business', icon: '📝', label: '입점 승인' },
            { id: 'users', icon: '👥', label: '회원 관리' },
            { id: 'dogs', icon: '🐶', label: '게시물 관리' },
            { id: 'reports', icon: '🚨', label: '신고 관리' },
            { id: 'adRequests', icon: '🛒', label: '서비스 구매 신청' },
            { id: 'payments', icon: '💳', label: '결제 관리' },
            { id: 'banners', icon: '🖼️', label: '배너 이미지 관리' },
            { id: 'coupons', icon: '🎫', label: '쿠폰 시스템' }
          ].map(item => (
            <div 
              key={item.id} 
              onClick={() => setActiveTab(item.id)}
              style={{ ...navItemStyle, backgroundColor: activeTab === item.id ? 'rgba(255,255,255,0.1)' : 'transparent' }}
            >
              <span>{item.icon}</span>
              <span style={{ marginLeft: '10px' }}>{item.label}</span>
            </div>
          ))}
        </div>
      </nav>

      <main style={{ flex: 1, padding: '40px', overflowY: 'auto' }}>
        <header style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '40px' }}>
          <h1 style={{ fontSize: '1.8rem', fontWeight: '800' }}>
            { activeTab === 'summary' && '실시간 활동 통계' }
            { activeTab === 'business' && '입점 신청 관리' }
            { activeTab === 'users' && '전체 회원 관리' }
            { activeTab === 'dogs' && '분양 게시물 관리' }
            { activeTab === 'reports' && '🚨 신고 내역 관리' }
            { activeTab === 'adRequests' && '🛒 서비스 신청 관리' }
            { activeTab === 'payments' && '💳 결제 관리' }
            { activeTab === 'coupons' && '쿠폰 시스템 관리' }
          </h1>
          <div style={{ backgroundColor: 'white', padding: '10px 20px', borderRadius: '30px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', fontSize: '0.9rem' }}>
            마지막 업데이트: {new Date().toLocaleTimeString()}
          </div>
        </header>

        {activeTab === 'summary' && (
          <div className="fade-in">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px', marginBottom: '30px' }}>
              <StatCard label="총 회원" value={stats.users} color="#4A90E2" />
              <StatCard label="승인 대기" value={stats.applications} color="#F5A623" />
              <StatCard label="활성 분양글" value={stats.dogs} color="#7ED321" />
              <StatCard label="누적 활동수" value={stats.clicks} color="#9013FE" />
            </div>

            <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #eee', paddingBottom: '30px', marginBottom: '30px' }}>
                <div>
                  <h2 style={{ fontSize: '1.5rem', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 20px 0' }}>
                    조회수 <span style={{ fontSize: '1rem', color: '#999', fontWeight: 'normal', cursor: 'pointer' }}>❔</span>
                  </h2>
                  <div>
                    <div style={{ color: '#888', fontSize: '0.95rem', marginBottom: '5px' }}>{new Date().toLocaleDateString('ko-KR')} 기준</div>
                    <div style={{ fontSize: '3rem', fontWeight: '800', letterSpacing: '-1px', color: '#111' }}>
                      {visitStats.todayCount}<span style={{ fontSize: '1.2rem', fontWeight: 'normal', color: '#666', marginLeft: '4px' }}>건</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', border: '1px solid #ddd', borderRadius: '6px', overflow: 'hidden' }}>
                  {['daily', 'weekly', 'monthly'].map(p => (
                    <button 
                      key={p} 
                      onClick={() => fetchVisitData(p)}
                      style={{ 
                        padding: '10px 24px', 
                        border: 'none', 
                        backgroundColor: visitPeriod === p ? '#00c73c' : 'white', 
                        color: visitPeriod === p ? 'white' : '#666',
                        fontWeight: visitPeriod === p ? '700' : '500',
                        fontSize: '0.95rem',
                        cursor: 'pointer',
                        borderRight: p !== 'monthly' ? '1px solid #ddd' : 'none',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {p === 'daily' ? '일간' : p === 'weekly' ? '주간' : '월간'}
                    </button>
                  ))}
                </div>
              </div>

              {/* 차트 영역 */}
              <div style={{ height: '350px', width: '100%', marginBottom: '50px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={visitStats.data} onClick={(e) => { if(e && e.activeLabel) fetchVisitData(visitPeriod, e.activeLabel === selectedDate ? null : e.activeLabel); }} style={{ cursor: 'pointer' }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="label" fontSize={12} stroke="#999" tickMargin={12} axisLine={false} tickLine={false} />
                    <YAxis fontSize={12} stroke="#999" axisLine={false} tickLine={false} />
                    <Tooltip 
                      contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 15px rgba(0,0,0,0.1)' }}
                      labelStyle={{ color: '#666', marginBottom: '5px' }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="count" 
                      name="접속자수" 
                      stroke="#00c73c" 
                      strokeWidth={3} 
                      dot={{ r: 4, fill: 'white', stroke: '#00c73c', strokeWidth: 2 }} 
                      activeDot={{ r: 6, fill: '#00c73c', stroke: 'white', strokeWidth: 2 }} 
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* 데이터 테이블 */}
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center', fontSize: '1rem' }}>
                <thead>
                  <tr style={{ borderTop: '2px solid #333', borderBottom: '1px solid #333', backgroundColor: '#fafafa' }}>
                    <th style={{ padding: '16px 10px', color: '#555', fontWeight: '600', width: '50%' }}>날짜</th>
                    <th style={{ padding: '16px 10px', color: '#555', fontWeight: '600', width: '50%' }}>전체 접속자수</th>
                  </tr>
                </thead>
                <tbody>
                  {[...visitStats.data].reverse().map((item, idx) => (
                    <tr 
                      key={idx} 
                      onClick={() => fetchVisitData(visitPeriod, item.label === selectedDate ? null : item.label)}
                      style={{ 
                        borderBottom: '1px solid #f0f0f0', 
                        cursor: 'pointer',
                        backgroundColor: selectedDate === item.label ? '#e8f5e9' : 'transparent',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseEnter={(e) => { if (selectedDate !== item.label) e.currentTarget.style.backgroundColor = '#fafafa'; }}
                      onMouseLeave={(e) => { if (selectedDate !== item.label) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <td style={{ padding: '16px 10px', color: '#444' }}>
                        {item.label} {selectedDate === item.label && <span style={{ color: '#00c73c', fontWeight: 'bold', marginLeft: '5px' }}>✓</span>}
                      </td>
                      <td style={{ padding: '16px 10px', color: '#222', fontWeight: '700' }}>{item.count}</td>
                    </tr>
                  ))}
                  {visitStats.data.length === 0 && (
                    <tr>
                      <td colSpan="2" style={{ padding: '50px', color: '#999', fontSize: '0.95rem' }}>데이터가 없습니다.</td>
                    </tr>
                  )}
                </tbody>
              </table>

              {/* Referrer & Keyword 테이블 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '40px' }}>
                <div>
                  <h3 style={{ fontSize: '1.2rem', marginBottom: '15px', color: '#333' }}>
                    🔗 유입 경로 (Referrer)
                    <span style={{ fontSize: '0.9rem', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                      {selectedDate ? `(${selectedDate} 기준)` : '(전체 기간)'}
                    </span>
                  </h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.95rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#fafafa' }}>
                        <th style={{ padding: '12px', color: '#555' }}>도메인 / URL</th>
                        <th style={{ padding: '12px', width: '80px', textAlign: 'center', color: '#555' }}>방문수</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(visitStats.referrers || []).map((ref, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '12px', wordBreak: 'break-all', color: '#444' }}>{ref.referrer}</td>
                          <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', color: '#222' }}>{ref.count}</td>
                        </tr>
                      ))}
                      {(!visitStats.referrers || visitStats.referrers.length === 0) && (
                        <tr><td colSpan="2" style={{ padding: '30px', textAlign: 'center', color: '#999' }}>데이터가 없습니다.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                <div>
                  <h3 style={{ fontSize: '1.2rem', marginBottom: '15px', color: '#333' }}>
                    🔍 검색 키워드 (Keyword)
                    <span style={{ fontSize: '0.9rem', fontWeight: 'normal', color: '#666', marginLeft: '10px' }}>
                      {selectedDate ? `(${selectedDate} 기준)` : '(전체 기간)'}
                    </span>
                  </h3>
                  <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '0.95rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '2px solid #ddd', backgroundColor: '#fafafa' }}>
                        <th style={{ padding: '12px', color: '#555' }}>키워드 / 검색어</th>
                        <th style={{ padding: '12px', width: '80px', textAlign: 'center', color: '#555' }}>방문수</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(visitStats.keywords || []).map((kw, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '12px', wordBreak: 'break-all', color: '#444' }}>{kw.keyword}</td>
                          <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold', color: '#222' }}>{kw.count}</td>
                        </tr>
                      ))}
                      {(!visitStats.keywords || visitStats.keywords.length === 0) && (
                        <tr><td colSpan="2" style={{ padding: '30px', textAlign: 'center', color: '#999' }}>데이터가 없습니다.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'business' && (
          <div className="glass-card fade-in" style={{ padding: '0', overflow: 'hidden' }}>
            <table style={tableStyle}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={thStyle}>신청일</th>
                  <th style={thStyle}>사업장명</th>
                  <th style={thStyle}>대표자</th>
                  <th style={thStyle}>연락처</th>
                  <th style={thStyle}>사업자등록번호</th>
                  <th style={thStyle}>동물판매업번호</th>
                  <th style={thStyle}>상태</th>
                  <th style={thStyle}>관리</th>
                </tr>
              </thead>
              <tbody>
                {applications.map(app => (
                  <tr key={app.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={tdStyle}>{new Date(app.created_at).toLocaleDateString()}</td>
                    <td style={tdStyle}><b>{app.business_name}</b></td>
                    <td style={tdStyle}>{app.representative_name}</td>
                    <td style={tdStyle}>{app.phone}</td>
                    <td style={tdStyle}>{app.biz_no}</td>
                    <td style={tdStyle}>{app.animal_sale_no || '-'}</td>
                    <td style={tdStyle}>
                      <span style={{ 
                        padding: '4px 10px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: '700',
                        backgroundColor: app.status === 'pending' ? '#fff4e5' : (app.status === 'approved' ? '#eefbe7' : '#ffeaea'),
                        color: app.status === 'pending' ? '#f5a623' : (app.status === 'approved' ? '#7ed321' : '#ff4757')
                      }}>
                        {app.status === 'pending' ? '승인 대기' : (app.status === 'approved' ? '승인 완료' : '반려')}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                        {app.status === 'pending' && (
                          <>
                            <button onClick={() => handleApprove(app)} style={tableBtnStyle}>승인</button>
                            <button onClick={() => handleReject(app)} style={{ ...tableBtnStyle, backgroundColor: '#ff4757' }}>반려</button>
                          </>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <button 
                            onClick={() => {
                              if (app.file_url) {
                                window.open(app.file_url);
                              } else {
                                alert('첨부된 사업자등록증 서류가 없습니다.');
                              }
                            }} 
                            style={{ 
                              ...tableBtnStyle, 
                              backgroundColor: app.file_url ? '#eee' : '#f5f5f5', 
                              color: app.file_url ? '#666' : '#ccc',
                              cursor: app.file_url ? 'pointer' : 'not-allowed',
                              fontSize: '0.75rem', padding: '4px 8px'
                            }}
                          >
                            사업자등록증
                          </button>
                          <button 
                            onClick={() => {
                              if (app.animal_sale_file_url) {
                                window.open(app.animal_sale_file_url);
                              } else {
                                alert('첨부된 동물판매업 등록증 서류가 없습니다.');
                              }
                            }} 
                            style={{ 
                              ...tableBtnStyle, 
                              backgroundColor: app.animal_sale_file_url ? '#eee' : '#f5f5f5', 
                              color: app.animal_sale_file_url ? '#666' : '#ccc',
                              cursor: app.animal_sale_file_url ? 'pointer' : 'not-allowed',
                              fontSize: '0.75rem', padding: '4px 8px'
                            }}
                          >
                            동물판매업증
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {activeTab === 'dogs' && (
          <div className="glass-card fade-in" style={{ padding: '0' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #eee' }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '800' }}>시스템 전체 게시물 목록</h3>
              <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '5px' }}>등록된 모든 강아지 분양 게시물을 관리합니다.</p>
            </div>
            <table style={tableStyle}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={thStyle}>사진</th>
                  <th style={thStyle}>견종/이름</th>
                  <th style={thStyle}>지역/가격</th>
                  <th style={thStyle}>등록일</th>
                  <th style={thStyle}>판매자 ID</th>
                  <th style={thStyle}>상태</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>바로가기</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>관리</th>
                </tr>
              </thead>
              <tbody>
                {dogs.map(dog => (
                  <tr key={dog.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={tdStyle}>
                      <img 
                        src={dog.images ? (typeof dog.images === 'string' && dog.images.startsWith('[') ? JSON.parse(dog.images)[0] : (Array.isArray(dog.images) ? dog.images[0] : dog.images)) : 'https://via.placeholder.com/50'} 
                        alt="dog" 
                        style={{ width: '50px', height: '50px', borderRadius: '5px', objectFit: 'cover', backgroundColor: '#f0f0f0' }}
                        onError={(e) => { e.target.src = 'https://via.placeholder.com/50'; }}
                      />
                    </td>
                    <td style={tdStyle}>
                      <strong>{dog.breed}</strong><br/>
                      <span style={{ fontSize: '0.8rem', color: '#888' }}>{dog.nickname}</span>
                    </td>
                    <td style={tdStyle}>{dog.region}<br/>{dog.price === 0 ? '무료' : `${dog.price}만`}</td>
                    <td style={tdStyle}>{new Date(dog.created_at).toLocaleDateString()}</td>
                    <td style={tdStyle}><small style={{ color: '#999' }}>{dog.seller_id.split('-')[0]}...</small></td>
                    <td style={tdStyle}>
                      <span style={{ padding: '4px 8px', backgroundColor: '#eefbe7', color: '#7ed321', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                        {dog.status === 'available' ? '분양중' : '분양완료'}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <button 
                        onClick={() => navigate(`/detail/${dog.id}`)} 
                        style={{ ...tableBtnStyle, backgroundColor: '#3498db', fontSize: '0.8rem', padding: '5px 10px' }}
                      >
                        게시물 보기
                      </button>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <button 
                        onClick={() => {
                          if(window.confirm('관리자 권한으로 이 게시물을 강제 삭제하시겠습니까?')) {
                             handleDeletePost(dog.id);
                          }
                        }} 
                        style={{ ...tableBtnStyle, backgroundColor: '#ff4757' }}
                      >
                        삭제
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {dogs.length === 0 && <div style={{ padding: '50px', textAlign: 'center', color: '#ccc' }}>등록된 게시물이 없습니다.</div>}
          </div>
        )}

        {activeTab === 'users' && (
          <div className="glass-card fade-in" style={{ padding: '0' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '800' }}>가입 회원 목록</h3>
              <button onClick={handleSendGlobalMessage} style={{ ...tableBtnStyle, backgroundColor: '#4A90E2', padding: '10px 15px', fontSize: '0.9rem' }}>📢 전체 회원에게 공지 발송</button>
            </div>
            <table style={tableStyle}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={thStyle}>이메일</th>
                  <th style={thStyle}>닉네임</th>
                  <th style={thStyle}>가입일</th>
                  <th style={thStyle}>권한</th>
                  <th style={thStyle}>등급</th>
                  <th style={thStyle}>등급 조정</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={tdStyle}>{u.email || 'OAuth 유저'}</td>
                    <td style={tdStyle}>{u.nickname}</td>
                    <td style={tdStyle}>{new Date(u.created_at).toLocaleDateString()}</td>
                    <td style={tdStyle}>{u.role}</td>
                    <td style={tdStyle}>
                      <span style={{ fontWeight: '700', color: u.grade === 'Gold' ? '#f5a623' : (u.grade === 'Silver' ? '#999' : '#cd7f32') }}>
                        [{u.grade || 'Bronze'}]
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <select 
                        value={u.grade || 'Bronze'} 
                        onChange={(e) => handleUpdateGrade(u.id, e.target.value)}
                        style={{ padding: '5px', borderRadius: '5px', border: '1px solid #ddd' }}
                      >
                        <option value="Bronze">Bronze</option>
                        <option value="Silver">Silver</option>
                        <option value="Gold">Gold</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'reports' && (
          <div className="glass-card fade-in" style={{ padding: '0' }}>
            <div style={{ padding: '20px', borderBottom: '1px solid #eee' }}>
              <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '800' }}>사용자 신고 접수 내역</h3>
              <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '5px' }}>허위 분양글이나 규정 위반으로 접수된 내역입니다.</p>
            </div>
            <table style={tableStyle}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={thStyle}>접수일시</th>
                  <th style={thStyle}>신고자</th>
                  <th style={thStyle}>대상 (판매자)</th>
                  <th style={thStyle}>사유</th>
                  <th style={thStyle}>상세내용</th>
                  <th style={thStyle}>상태</th>
                  <th style={thStyle}>관리</th>
                </tr>
              </thead>
              <tbody>
                {reports.map(report => (
                  <tr key={report.id} style={{ borderBottom: '1px solid #eee', opacity: report.status === 'resolved' ? 0.6 : 1 }}>
                    <td style={tdStyle}>{new Date(report.created_at).toLocaleString()}</td>
                    <td style={tdStyle}>{report.reporter_nickname || '알 수 없음'} ({report.reporter_email || '-'})</td>
                    <td style={tdStyle}>
                      <b>{report.target_dog_breed || report.target_dog_name || `ID: ${report.dog_id}`}</b>
                      <br/><small style={{color: '#888'}}>판매자: {report.seller_nickname || '알 수 없음'} ({report.seller_email || '-'})</small>
                    </td>
                    <td style={tdStyle}><span style={{ color: '#e63946', fontWeight: 'bold' }}>{report.reason_type}</span></td>
                    <td style={tdStyle}><div style={{ maxWidth: '200px', fontSize: '0.85rem' }}>{report.details || '-'}</div></td>
                    <td style={tdStyle}>
                      <span style={{ 
                        padding: '4px 8px', borderRadius: '4px', fontSize: '0.75rem',
                        backgroundColor: report.status === 'pending' ? '#fff4e5' : '#eee',
                        color: report.status === 'pending' ? '#f5a623' : '#666'
                      }}>
                        {report.status === 'pending' ? '처리 대기' : '조치 완료'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <div style={{ display: 'flex', gap: '5px' }}>
                        {report.status === 'pending' && (
                          <>
                            <button onClick={() => handleDeleteReportedDog(report)} style={{ ...tableBtnStyle, backgroundColor: '#ff4757' }}>삭제</button>
                            <button onClick={() => handleResolveReport(report.id)} style={{ ...tableBtnStyle, backgroundColor: '#eee', color: '#666' }}>반려</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {reports.length === 0 && (
                  <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#888' }}>접수된 신고 내역이 없습니다.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'adRequests' && (
          <div className="fade-in glass-card" style={{ padding: '30px' }}>
            <h3 style={{ marginBottom: '20px', fontWeight: '800' }}>🛒 서비스 이용권 구매 신청 내역</h3>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>신청일시</th>
                  <th style={thStyle}>노출 영역</th>
                  <th style={thStyle}>상점/신청자</th>
                  <th style={thStyle}>사업자정보</th>
                  <th style={thStyle}>결제금액</th>
                  <th style={thStyle}>상태</th>
                  <th style={thStyle}>관리 액션</th>
                </tr>
              </thead>
              <tbody>
                {adRequests.filter(req => req.price > 0).map(req => (
                  <tr key={req.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={tdStyle}>{new Date(req.created_at).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                    <td style={{ ...tdStyle, fontWeight: 'bold', color: 'var(--primary-dark)' }}>{req.title}</td>
                    <td style={tdStyle}>
                      {req.business_name || req.nickname}
                      {req.representative_name && <span style={{ fontSize: '0.85rem', color: '#666', marginLeft: '4px' }}>({req.representative_name})</span>}
                      <br/>
                      <span style={{ fontSize: '0.8rem', color: '#888' }}>{req.email}</span>
                      {req.phone && <><br/><span style={{ fontSize: '0.8rem', color: '#888' }}>{req.phone}</span></>}
                    </td>
                    <td style={tdStyle}>{req.biz_no || '미등록'}</td>
                    <td style={{ ...tdStyle, fontWeight: 'bold' }}>{req.price?.toLocaleString()}원</td>
                    <td style={tdStyle}>
                      {req.status === 'pending' && <span style={{ ...badgeStyle, backgroundColor: '#f39c12' }}>입금대기</span>}
                      {req.status === 'active' && <span style={{ ...badgeStyle, backgroundColor: '#2ecc71' }}>승인/발행완료</span>}
                      {req.status === 'rejected' && <span style={{ ...badgeStyle, backgroundColor: '#e74c3c' }}>반려/취소</span>}
                    </td>
                    <td style={tdStyle}>
                      {req.status === 'pending' && (
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button onClick={() => handleUpdateAdRequestStatus(req.id, 'active')} style={{ ...tableBtnStyle, backgroundColor: 'var(--primary)' }}>확인(세금계산서)</button>
                          <button onClick={() => handleUpdateAdRequestStatus(req.id, 'rejected')} style={{ ...tableBtnStyle, backgroundColor: '#eee', color: '#666' }}>취소</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {adRequests.filter(req => req.price > 0).length === 0 && (
                  <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#888' }}>접수된 서비스 구매 신청 내역이 없습니다.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'adUsages' && (
          <div className="fade-in glass-card" style={{ padding: '30px' }}>
            <h3 style={{ marginBottom: '20px', fontWeight: '800' }}>🎯 서비스 이용권 사용(적용) 내역</h3>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>사용일시</th>
                  <th style={thStyle}>노출 영역</th>
                  <th style={thStyle}>사용자</th>
                  <th style={thStyle}>상태</th>
                </tr>
              </thead>
              <tbody>
                {adRequests.filter(req => !req.price || req.price === 0).map(req => (
                  <tr key={req.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={tdStyle}>{new Date(req.created_at).toLocaleString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                    <td style={{ ...tdStyle, fontWeight: 'bold', color: 'var(--primary-dark)' }}>{req.title}</td>
                    <td style={tdStyle}>
                      {req.business_name || req.nickname}
                      <br/>
                      <span style={{ fontSize: '0.8rem', color: '#888' }}>{req.email}</span>
                      {req.phone && <><br/><span style={{ fontSize: '0.8rem', color: '#888' }}>{req.phone}</span></>}
                    </td>
                    <td style={tdStyle}>
                      {req.status === 'pending' && <span style={{ ...badgeStyle, backgroundColor: '#f39c12' }}>대기중</span>}
                      {req.status === 'active' && <span style={{ ...badgeStyle, backgroundColor: '#2ecc71' }}>적용중/완료</span>}
                      {req.status === 'rejected' && <span style={{ ...badgeStyle, backgroundColor: '#e74c3c' }}>취소/종료</span>}
                    </td>
                  </tr>
                ))}
                {adRequests.filter(req => !req.price || req.price === 0).length === 0 && (
                  <tr><td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#888' }}>서비스 이용권 사용 내역이 없습니다.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'coupons' && (
          <div className="fade-in">
            {/* 1. 쿠폰 템플릿 생성 섹션 */}
            <div className="glass-card" style={{ padding: '30px', marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '20px', fontWeight: '800' }}>✨ 새 쿠폰 템플릿 생성</h3>
              <form onSubmit={handleCreateCoupon} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '15px' }}>
                <div>
                  <label style={smallLabel}>쿠폰명</label>
                  <input required style={adminInput} value={newCoupon.name} onChange={e => setNewCoupon({...newCoupon, name: e.target.value})} placeholder="신규 가입 감사 쿠폰" />
                </div>
                <div>
                  <label style={smallLabel}>쿠폰 코드</label>
                  <input required style={adminInput} value={newCoupon.code} onChange={e => setNewCoupon({...newCoupon, code: e.target.value})} placeholder="WELCOME2024" />
                </div>
                <div>
                  <label style={smallLabel}>서비스 보장 기간 (일수)</label>
                  <input required type="number" style={adminInput} value={newCoupon.amount} onChange={e => setNewCoupon({...newCoupon, amount: e.target.value})} />
                </div>
                <div>
                  <label style={smallLabel}>적용 가능 구역</label>
                  <select style={adminInput} value={newCoupon.ad_type} onChange={e => setNewCoupon({...newCoupon, ad_type: e.target.value})}>
                    <option value="all">전체 구역 가능</option>
                    <option value="main">메인페이지 메인배너 전용</option>
                    <option value="breed">품종별 페이지 메인배너 전용</option>
                    <option value="safe">메인페이지 안심분양 전용</option>
                    <option value="popular">메인페이지 인기분양 전용</option>
                    <option value="special">메인페이지 스페셜분양 전용</option>
                  </select>
                </div>
                <div>
                  <label style={smallLabel}>혜택 종류</label>
                  <select style={adminInput} value={newCoupon.benefit_type} onChange={e => {
                    const val = e.target.value;
                    setNewCoupon({
                      ...newCoupon, 
                      benefit_type: val, 
                      amount: val.startsWith('post_reg') ? 20 : newCoupon.amount
                    });
                  }}>
                    <option value="ad_exemption">일반 서비스 이용료 면제권</option>
                    <option value="ad_main">메인 페이지 프리미엄 노출권</option>
                    <option value="ad_safe_1m">안심분양 프리미엄 (1개월)</option>
                    <option value="ad_popular_1m">인기분양 프리미엄 (1개월)</option>
                    <option value="ad_special_1m">스페셜분양 프리미엄 (1개월)</option>
                    <option value="post_reg_1m">게시물 등록 1개월 쿠폰 (1달 20개)</option>
                    <option value="post_reg_2m">게시물 등록 2개월 쿠폰 (1달 20개)</option>
                    <option value="post_reg_3m">게시물 등록 3개월 쿠폰 (1달 20개)</option>
                    <option value="post_reg_4m">게시물 등록 4개월 쿠폰 (1달 20개)</option>
                    <option value="post_reg_5m">게시물 등록 5개월 쿠폰 (1달 20개)</option>
                    <option value="post_reg_6m">게시물 등록 6개월 쿠폰 (1달 20개)</option>
                  </select>
                </div>
                <div>
                  <label style={smallLabel}>유효 기한 (설정 시 이후 자동 소멸)</label>
                  <input type="date" style={adminInput} value={newCoupon.valid_until} onChange={e => setNewCoupon({...newCoupon, valid_until: e.target.value})} />
                </div>
                <div>
                  <label style={smallLabel}>자동 발급 설정</label>
                  <select style={adminInput} value={newCoupon.auto_issue_type} onChange={e => setNewCoupon({...newCoupon, auto_issue_type: e.target.value})}>
                    <option value="none">수동 발급용 (자동없음)</option>
                    <option value="welcome">사업자 승인 시 자동발급</option>
                  </select>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end' }}>
                  <button type="submit" disabled={isCreating} style={{ ...createBtnStyle, width: '100%', height: '45px', backgroundColor: isCreating ? '#999' : '#2D3436', cursor: isCreating ? 'not-allowed' : 'pointer' }}>
                    {isCreating ? '등록 중...' : '쿠폰 템플릿 등록'}
                  </button>
                </div>
              </form>
            </div>

            {/* 2. 쿠폰 개별 발급 섹션 */}
            <div className="glass-card" style={{ padding: '30px', marginBottom: '30px', border: '2px solid #eef2f7' }}>
              <h3 style={{ marginBottom: '20px', fontWeight: '800', color: 'var(--primary-dark)' }}>🎁 사업자에게 쿠폰 선물하기</h3>
              <form onSubmit={handleIssueCoupon} style={{ display: 'grid', gap: '20px' }}>
                
                {/* 2-1 발송 대상 형태 선택 */}
                <div style={{ display: 'flex', gap: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                    <input type="radio" name="targetType" value="individual" checked={issueTargetType === 'individual'} onChange={() => setIssueTargetType('individual')} />
                    특정 사업자 선택 (개별 발송)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                    <input type="radio" name="targetType" value="all_sellers" checked={issueTargetType === 'all_sellers'} onChange={() => setIssueTargetType('all_sellers')} />
                    모든 사업자 전체 발송
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  {issueTargetType === 'individual' && (
                    <>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <label style={smallLabel}>검색어로 찾기 (이름/이메일)</label>
                        <input 
                          type="text" 
                          style={adminInput} 
                          placeholder="검색어를 입력하세요..." 
                          value={userSearch} 
                          onChange={(e) => setUserSearch(e.target.value)} 
                        />
                      </div>
                      <div style={{ flex: 1, minWidth: '200px' }}>
                        <label style={smallLabel}>대상 사업자 선택</label>
                        <select required style={adminInput} value={issueData.userId} onChange={e => setIssueData({...issueData, userId: e.target.value})}>
                          <option value="">사업자를 선택하세요</option>
                          {users
                            .filter(u => u.role === 'seller') // 일반유저 제외
                            .filter(u => userSearch === '' || 
                               (u.nickname && u.nickname.includes(userSearch)) || 
                               (u.email && u.email.includes(userSearch))
                            )
                            .map(u => (
                              <option key={u.id} value={u.id}>{u.nickname} ({u.email || 'OAuth'})</option>
                          ))}
                        </select>
                      </div>
                    </>
                  )}

                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <label style={smallLabel}>발급할 쿠폰 선택</label>
                    <select required style={adminInput} value={issueData.couponId} onChange={e => setIssueData({...issueData, couponId: e.target.value})}>
                      <option value="">쿠폰을 선택하세요</option>
                      {coupons.map(c => (
                        <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                      ))}
                    </select>
                  </div>

                  <button type="submit" disabled={isIssuing} style={{ ...createBtnStyle, backgroundColor: isIssuing ? '#999' : 'var(--primary-dark)', height: '45px', padding: '0 30px', cursor: isIssuing ? 'not-allowed' : 'pointer' }}>
                    {isIssuing ? '발급 중...' : '쿠폰 발급하기'}
                  </button>
                </div>
              </form>
            </div>

            {/* 3. 현재 등록된 쿠폰 목록 (심플) */}
            <div className="glass-card" style={{ padding: '20px' }}>
              <h4 style={{ marginBottom: '15px' }}>현재 시스템 쿠폰 목록</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
                {coupons.map(c => (
                  <div key={c.id} style={{ padding: '15px', border: '1px solid #eee', borderRadius: '10px', backgroundColor: '#f9f9f9' }}>
                    <div style={{ fontWeight: 'bold' }}>{c.name}</div>
                    <div style={{ fontSize: '0.8rem', color: '#666' }}>코드: {c.code}</div>
                    <div style={{ fontSize: '0.75rem', marginTop: '5px', color: 'var(--primary)' }}>
                      {c.auto_issue_type === 'welcome' ? '✅ 사업자 웰컴 자동발급' : '👤 수동 발급용'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ========================================================
            배너 관리 탭
        ======================================================== */}
        {activeTab === 'banners' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2>🖼️ 배너 이미지 관리</h2>
            </div>
            
            <div className="glass-card" style={{ padding: '25px', marginBottom: '30px' }}>
              <h3 style={{ marginBottom: '15px' }}>새 배너 등록하기</h3>
              <form onSubmit={handleBannerSubmit}>
                <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1, minWidth: '200px' }}>
                    <label style={smallLabel}>적용할 위치 (슬롯)</label>
                    <select required style={adminInput} value={bannerUploadForm.slot_key} onChange={e => setBannerUploadForm({...bannerUploadForm, slot_key: e.target.value})}>
                      <option value="">슬롯을 선택하세요</option>
                      <option value="main_sidebar">메인페이지 사이드 배너 (300x250)</option>
                      <option value="main_bottom_a">메인페이지 하단 배너 A (1200x140)</option>
                      <option value="main_bottom_b">메인페이지 하단 배너 B (1200x140)</option>
                      <option value="breed_sidebar">품종별페이지 사이드 배너 (300x250)</option>
                    </select>
                  </div>
                  <div style={{ flex: 2, minWidth: '300px' }}>
                    <label style={smallLabel}>배너 이미지 첨부 (또는 URL 입력)</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <input 
                        id="banner-file-input"
                        type="file" 
                        accept="image/*"
                        style={adminInput}
                        onChange={e => setBannerUploadFile(e.target.files[0])}
                      />
                      <input 
                        type="url" 
                        style={adminInput} 
                        placeholder="직접 URL 입력 시 (예: https://...)" 
                        value={bannerUploadForm.image_url} 
                        onChange={e => setBannerUploadForm({...bannerUploadForm, image_url: e.target.value})} 
                        disabled={!!bannerUploadFile}
                      />
                    </div>
                  </div>
                  <div style={{ flex: 2, minWidth: '300px' }}>
                    <label style={smallLabel}>클릭 시 이동할 링크 URL (선택)</label>
                    <input 
                      type="url" 
                      style={adminInput} 
                      placeholder="https://... (클릭 시 이동할 주소)" 
                      value={bannerUploadForm.link_url} 
                      onChange={e => setBannerUploadForm({...bannerUploadForm, link_url: e.target.value})} 
                    />
                  </div>
                  <button type="submit" disabled={isBannerUploading} style={{ ...createBtnStyle, backgroundColor: isBannerUploading ? '#999' : 'var(--primary-dark)', cursor: isBannerUploading ? 'not-allowed' : 'pointer' }}>
                    {isBannerUploading ? '업로드 중...' : '배너 등록'}
                  </button>
                </div>
              </form>
            </div>

            <h3 style={{ marginBottom: '15px' }}>등록된 배너 현황 (각 슬롯 최대 3개)</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {[
                { key: 'main_sidebar', name: '메인페이지 사이드 배너', size: '300x250' },
                { key: 'main_bottom_a', name: '메인페이지 하단 배너 A', size: '1200x140' },
                { key: 'main_bottom_b', name: '메인페이지 하단 배너 B', size: '1200x140' },
                { key: 'breed_sidebar', name: '품종별페이지 사이드 배너', size: '300x250' }
              ].map(slot => (
                <div key={slot.key} className="glass-card" style={{ padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                    <h4 style={{ margin: 0 }}>{slot.name}</h4>
                    <span style={badgeStyle}>
                      {banners[slot.key]?.length || 0} / 3
                    </span>
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#666', marginBottom: '15px' }}>권장: {slot.size}</div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {banners[slot.key] && banners[slot.key].length > 0 ? (
                      banners[slot.key].map(banner => (
                        <div key={banner.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px', border: '1px solid #eee', borderRadius: '8px', backgroundColor: 'white' }}>
                          <div style={{ width: '80px', height: '40px', overflow: 'hidden', borderRadius: '4px', border: '1px solid #ddd', flexShrink: 0 }}>
                            <img src={banner.image_url} alt="banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </div>
                          <div style={{ flex: 1, overflow: 'hidden' }}>
                            <div style={{ fontSize: '0.75rem', color: '#888', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                              이미지: {banner.image_url}
                            </div>
                            {banner.link_url && (
                              <div style={{ fontSize: '0.75rem', color: 'var(--primary)', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                링크: {banner.link_url}
                              </div>
                            )}
                          </div>
                          <button onClick={() => handleBannerDelete(banner.id)} style={{ padding: '5px 10px', backgroundColor: '#ff4757', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.75rem' }}>
                            삭제
                          </button>
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: '20px', textAlign: 'center', color: '#999', fontSize: '0.85rem', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
                        등록된 배너가 없습니다.
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="glass-card fade-in" style={{ padding: '0', overflow: 'hidden' }}>
            <table style={tableStyle}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa' }}>
                  <th style={thStyle}>결제일시</th>
                  <th style={thStyle}>결제번호(주문번호)</th>
                  <th style={thStyle}>회원정보</th>
                  <th style={thStyle}>금액</th>
                  <th style={thStyle}>결제수단</th>
                  <th style={thStyle}>상태</th>
                  <th style={thStyle}>관리</th>
                </tr>
              </thead>
              <tbody>
                {payments.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{ padding: '30px', textAlign: 'center', color: '#888' }}>
                      결제 내역이 없습니다.
                    </td>
                  </tr>
                ) : (
                  payments.map(pay => (
                    <tr key={pay.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={tdStyle}>{new Date(pay.created_at).toLocaleString()}</td>
                      <td style={tdStyle}><small>{pay.merchant_uid}</small></td>
                      <td style={tdStyle}>{pay.nickname || '알 수 없음'} <br/><small style={{color:'#999'}}>{pay.email}</small></td>
                      <td style={tdStyle}><b>{pay.amount?.toLocaleString()}원</b></td>
                      <td style={tdStyle}>{pay.pay_method}</td>
                      <td style={tdStyle}>
                        {pay.status === 'paid' && <span style={{ ...badgeStyle, backgroundColor: '#7ED321' }}>결제완료</span>}
                        {pay.status === 'ready' && <span style={{ ...badgeStyle, backgroundColor: '#F5A623' }}>입금대기</span>}
                        {pay.status === 'cancelled' && <span style={{ ...badgeStyle, backgroundColor: '#999' }}>취소완료</span>}
                        {pay.status === 'failed' && <span style={{ ...badgeStyle, backgroundColor: '#D0021B' }}>실패</span>}
                        {pay.status === 'cancel_requested' && <span style={{ ...badgeStyle, backgroundColor: '#E056FD' }}>취소요청</span>}
                      </td>
                      <td style={tdStyle}>
                        {pay.status === 'cancel_requested' && (
                          <button onClick={() => handleApproveCancelPayment(pay)} style={{ ...tableBtnStyle, backgroundColor: '#E056FD' }}>
                            취소 승인
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className="glass-card" style={{ padding: '25px', display: 'flex', flexDirection: 'column', borderLeft: `5px solid ${color}` }}>
    <span style={{ color: '#888', fontSize: '0.9rem', marginBottom: '10px' }}>{label}</span>
    <span style={{ fontSize: '1.8rem', fontWeight: '800', color: '#333' }}>{(value || 0).toLocaleString()}</span>
  </div>
);

const sidebarStyle = { width: '260px', backgroundColor: '#2D3436', color: 'white', display: 'flex', flexDirection: 'column' };
const navItemStyle = { padding: '15px 30px', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', fontSize: '0.95rem' };
const fullCenterStyle = { height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: '700' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', textAlign: 'left' };
const thStyle = { padding: '15px 20px', fontSize: '0.85rem', color: '#666', borderBottom: '2px solid #eee' };
const tdStyle = { padding: '15px 20px', fontSize: '0.9rem', color: '#444' };
const tableBtnStyle = { padding: '5px 12px', borderRadius: '5px', border: 'none', backgroundColor: 'var(--primary)', color: 'white', fontSize: '0.8rem', fontWeight: '700', cursor: 'pointer' };
const adminInput = { width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #eee', outline: 'none' };
const smallLabel = { display: 'block', fontSize: '0.75rem', color: '#777', marginBottom: '5px' };
const badgeStyle = { padding: '4px 8px', borderRadius: '12px', color: 'white', fontSize: '0.75rem', fontWeight: 'bold' };
const createBtnStyle = { padding: '12px 30px', borderRadius: '8px', border: 'none', backgroundColor: '#2D3436', color: 'white', fontWeight: '800', cursor: 'pointer' };

export default AdminPage;
